import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, isRef as _isRef } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "d-flex align-items-center justify-content-center text-center",
  style: {"min-height":"200px"}
}
const _hoisted_2 = { key: 2 }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { class: "my-5 py-5 text-center" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { class: "d-flex justify-content-end mb-2" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { key: 1 }
const _hoisted_10 = { key: 2 }
const _hoisted_11 = { class: "text-end" }
const _hoisted_12 = { key: 1 }

import { ref, computed, watch } from 'vue';
import { refDebounced } from '@vueuse/core';
import { useQuery, useMutation } from '@vue/apollo-composable';
import {
  BucketsSamplesDocument,
  BucketType,
  ProjectSampleAssociationCreateDocument,
  ProjectSampleAssociationNewFragmentDoc,
  ProjectSampleAssociationType,
  SampleType,
} from '@/gql/graphql';
import {
  BAlert,
  BAccordion,
  BAccordionItem,
  BButton,
  BFormInput,
  BModal,
  BPagination,
  BSpinner,
  BTable,
} from 'bootstrap-vue-next';
import { BUCKET_DATA_TYPES, PAGING_ITEMS_PER_PAGE } from '@/constants';
import { deepParseDates } from '@/utils/date-utils';

// --- Component properties ---

export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectSampleAssociationCreate',
  props: {
    show: { type: Boolean },
    projectId: {},
    projectName: {}
  },
  emits: ['close', 'closeAndRefresh'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// --- Component state ---
const batchOperationInProgress = ref<boolean>(false);
const batchOperationShowSuccess = ref<boolean>(false);
const pagingPageCurrent = ref<number>(1);
const pagingTotalRows = computed(() => {
  return qResult.value?.buckets?.total || 0;
});
const bucketFriendlyNameSearchPattern = ref<string>('');
const debouncedFilter = refDebounced(bucketFriendlyNameSearchPattern, 400);

// --- Component emits ---
const emits = __emit;

const show = computed<boolean>(() => {
  return props.show;
});

// --- Query User Samples by Buckets ---
const qVars = computed(() => ({
  dataType: BUCKET_DATA_TYPES.INPUT,
  friendlyNamePattern: debouncedFilter.value || '',
  pagingPageCurrent: pagingPageCurrent.value,
  pagingItemsPerPage: PAGING_ITEMS_PER_PAGE,
}));

const {
  result: qResult,
  loading: qInProgress,
  error: qError,
  refetch: refetchBuckets,
} = useQuery(BucketsSamplesDocument, qVars, {
  fetchPolicy: 'network-only',
  enabled: show,
});

watch(pagingPageCurrent, () => {
  refetchBuckets(qVars.value);
});

watch(debouncedFilter, () => {
  pagingPageCurrent.value = 1; // reset paging when the search filter changes
});

// --- List of Buckets ---
const buckets = computed<BucketType[]>(() => {
  const rawList = qResult.value?.buckets?.buckets || [];
  const fineList = rawList
    .filter((b) => b !== null)
    .map((b) => deepParseDates(b) as BucketType);
  console.debug('buckets query result updated, count=', fineList.length);
  return fineList;
});

// --- Sample Table Fields ---
const sampleTableFields = computed(() => [
  {
    key: 'name',
    label: 'Sample Name',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'actions',
    label: '',
    tdClass: 'align-middle text-end min-width',
  },
]);

// --- Mutation: Create Project-Sample Association ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);
const mutationsSuccessful = ref(0);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(ProjectSampleAssociationCreateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.projectSampleAssociationCreate) {
    console.debug('Project-sample association created successfully');
    mutationShowSuccess.value = true;
    mutationsSuccessful.value++;
    setTimeout(() => {
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    mutationError.value = 'Project-sample association failed: ' + res.data;
    console.error('Project-sample association failed:', res.data);
  }
});

onMutationError((err) => {
  console.error('Error creating project-user association:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function sampleAlreadyAssociated(sample: SampleType): boolean {
  return (
    sample.projectsAssociated !== undefined &&
    sample.projectsAssociated !== null &&
    sample.projectsAssociated?.some(
      (assoc) => assoc && assoc.projectId === props.projectId
    )
  );
}

async function selectAllSamples(samples: SampleType[]) {
  if (samples.length === 0) {
    return;
  }
  batchOperationInProgress.value = true;
  for (const sample of samples) {
    if (!sampleAlreadyAssociated(sample)) {
      // wait for previous mutation to finish
      while (mutationInProgress.value) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      // initiate new mutation
      onProjectSampleAssociationRequested(sample);
    }
  }
  batchOperationShowSuccess.value = true;
  setTimeout(() => {
    batchOperationShowSuccess.value = false;
  }, 3000);
  batchOperationInProgress.value = false;
}

function onProjectSampleAssociationRequested(sample: SampleType) {
  if (!sample.id) {
    mutationError.value = 'Sample not provided';
    console.error('Sample not provided');
    return;
  }

  if (mutationInProgress.value) {
    console.warn('Mutation already in progress, please wait');
    return;
  }

  // Prevent duplicate associations
  if (sampleAlreadyAssociated(sample)) {
    console.error('Sample is already associated with this project');
    return;
  }

  // Continue with association creation
  console.debug(
    'Creating project-sample association, project ',
    props.projectId,
    'sample',
    sample.id
  );

  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation(
    { projectId: props.projectId, sampleId: sample.id },
    {
      update(cache, { data }) {
        // Find the assoc returned by the mutation (server or optimistic)
        const assoc =
          data?.projectSampleAssociationCreate?.samplesAssociated?.find(
            (a) => a?.sample?.id === sample.id
          );

        if (!assoc || !assoc.id) {
          console.warn(
            'Association or its ID not returned by mutation, cannot update cache'
          );
          return;
        }

        // Build the sample entity id
        const sampleEntityId = cache.identify({
          __typename: 'SampleType',
          id: sample.id,
        });
        if (!sampleEntityId) {
          console.warn(
            'Could not identify sample in cache, cannot update cache'
          );
          return;
        }

        // Build the association entity id
        const assocEntityId = cache.identify({
          __typename: 'ProjectSampleAssociationType',
          id: assoc.id, // or { projectId, sampleId } if using composite keyFields
        });
        if (!assocEntityId) {
          console.warn(
            'Could not identify project-sample association in cache, cannot update cache'
          );
          return;
        }

        // Ensure the association entity exists in cache (minimal fields)
        const newFragmentData: ProjectSampleAssociationType = {
          __typename: 'ProjectSampleAssociationType',
          id: assoc.id,
          projectId: props.projectId,
          sampleId: sample.id,
        };

        const assocRef = cache.writeFragment({
          id: assocEntityId, // <-- write to the association entity, NOT the sample
          fragment: ProjectSampleAssociationNewFragmentDoc, // must be ON ProjectSampleAssociationType
          data: newFragmentData,
        });

        // Append the association reference to Sample.projectsAssociated
        cache.modify({
          id: sampleEntityId,
          fields: {
            projectsAssociated(existingRefs = [], { readField }) {
              const exists = existingRefs.some(
                (ref: any) => readField('projectId', ref) === props.projectId
              );
              return exists ? existingRefs : [...existingRefs, assocRef];
            },
          },
        });
      },
    }
  );
}

function closeModal() {
  if (mutationsSuccessful.value > 0) {
    emits('closeAndRefresh');
  } else {
    emits('close');
  }
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BModal), {
      modelValue: show.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((show).value = $event)),
      onHide: closeModal
    }, {
      title: _withCtx(() => [
        _createVNode(_component_font_awesome_icon, {
          icon: "plus",
          class: "me-2"
        }),
        _cache[7] || (_cache[7] = _createTextVNode(" Add samples to project ", -1)),
        _createElementVNode("strong", null, _toDisplayString(props.projectName), 1)
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(BButton), {
          variant: "secondary",
          onClick: closeModal
        }, {
          default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
            _createTextVNode(" Close ", -1)
          ]))]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          _createVNode(_unref(BFormInput), {
            modelValue: bucketFriendlyNameSearchPattern.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((bucketFriendlyNameSearchPattern).value = $event)),
            type: "search",
            placeholder: "Filter data by name...",
            class: "mb-3"
          }, null, 8, ["modelValue"]),
          (pagingTotalRows.value > _unref(PAGING_ITEMS_PER_PAGE))
            ? (_openBlock(), _createBlock(_unref(BPagination), {
                key: 0,
                modelValue: pagingPageCurrent.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((pagingPageCurrent).value = $event)),
                "total-rows": pagingTotalRows.value,
                "per-page": _unref(PAGING_ITEMS_PER_PAGE),
                pills: "",
                size: "sm",
                class: "mb-2"
              }, null, 8, ["modelValue", "total-rows", "per-page"]))
            : _createCommentVNode("", true),
          (_unref(qInProgress))
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(BSpinner), { label: "Loading data" })
              ]))
            : _createCommentVNode("", true),
          (!_unref(qInProgress))
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                (!buckets.value.length)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      _createElementVNode("h5", _hoisted_4, [
                        (_unref(debouncedFilter))
                          ? (_openBlock(), _createElementBlock("span", _hoisted_5, " No data found matching the filter criteria. "))
                          : (_openBlock(), _createElementBlock("span", _hoisted_6, " There are no data in your collection. "))
                      ])
                    ]))
                  : _createCommentVNode("", true),
                (buckets.value.length > 0)
                  ? (_openBlock(), _createBlock(_unref(BAccordion), { key: 1 }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(buckets.value, (bucket) => {
                          return (_openBlock(), _createBlock(_unref(BAccordionItem), {
                            key: bucket.id,
                            id: `bucket-${bucket.id}`,
                            "button-class": "accordion-title"
                          }, {
                            title: _withCtx(() => [
                              _createElementVNode("span", null, [
                                _createVNode(_component_font_awesome_icon, {
                                  icon: "database",
                                  class: "me-2"
                                }),
                                _createTextVNode(" " + _toDisplayString(bucket.friendlyName), 1)
                              ])
                            ]),
                            default: _withCtx(() => [
                              (bucket.samples && bucket.samples.length > 0)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    _createElementVNode("div", _hoisted_7, [
                                      _createVNode(_unref(BButton), {
                                        disabled: 
                      mutationInProgress.value ||
                      batchOperationInProgress.value ||
                      batchOperationShowSuccess.value
                    ,
                                        variant: "outline-primary",
                                        size: "sm",
                                        onClick: ($event: any) => (selectAllSamples(bucket.samples as SampleType[]))
                                      }, {
                                        default: _withCtx(() => [
                                          (batchOperationInProgress.value)
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_8, [
                                                _cache[8] || (_cache[8] = _createTextVNode(" Sample association ", -1)),
                                                _createVNode(_unref(BSpinner), {
                                                  small: "",
                                                  class: "ms-2"
                                                })
                                              ]))
                                            : (batchOperationShowSuccess.value)
                                              ? (_openBlock(), _createElementBlock("span", _hoisted_9, [
                                                  _cache[9] || (_cache[9] = _createTextVNode(" Success ", -1)),
                                                  _createVNode(_component_font_awesome_icon, {
                                                    icon: ['fas', 'check-double'],
                                                    class: "ms-2"
                                                  })
                                                ]))
                                              : (_openBlock(), _createElementBlock("span", _hoisted_10, " Select All "))
                                        ]),
                                        _: 1
                                      }, 8, ["disabled", "onClick"])
                                    ]),
                                    _createVNode(_unref(BTable), {
                                      items: bucket.samples,
                                      fields: sampleTableFields.value,
                                      "primary-key": "id",
                                      small: "",
                                      responsive: "",
                                      hover: "",
                                      class: "browser-table"
                                    }, {
                                      "cell(name)": _withCtx((data) => [
                                        _createVNode(_component_font_awesome_icon, {
                                          icon: "flask",
                                          class: "me-2"
                                        }),
                                        _createTextVNode(" " + _toDisplayString(data.item.name), 1)
                                      ]),
                                      "cell(actions)": _withCtx((data) => [
                                        _createElementVNode("div", _hoisted_11, [
                                          _createVNode(_unref(BButton), {
                                            variant: "outline-primary",
                                            size: "sm",
                                            onClick: ($event: any) => (onProjectSampleAssociationRequested(data.item)),
                                            disabled: sampleAlreadyAssociated(data.item)
                                          }, {
                                            default: _withCtx(() => [
                                              _createVNode(_component_font_awesome_icon, {
                                                icon: 
                            sampleAlreadyAssociated(data.item)
                              ? 'check'
                              : 'plus'
                          
                                              }, null, 8, ["icon"])
                                            ]),
                                            _: 2
                                          }, 1032, ["onClick", "disabled"])
                                        ])
                                      ]),
                                      _: 1
                                    }, 8, ["items", "fields"])
                                  ], 64))
                                : (_openBlock(), _createElementBlock("small", _hoisted_12, " There are no samples in this bucket. "))
                            ]),
                            _: 2
                          }, 1032, ["id"]))
                        }), 128))
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          (pagingTotalRows.value > _unref(PAGING_ITEMS_PER_PAGE) && !_unref(qInProgress))
            ? (_openBlock(), _createBlock(_unref(BPagination), {
                key: 3,
                modelValue: pagingPageCurrent.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((pagingPageCurrent).value = $event)),
                "total-rows": pagingTotalRows.value,
                "per-page": _unref(PAGING_ITEMS_PER_PAGE),
                pills: "",
                size: "sm",
                class: "mt-2"
              }, null, 8, ["modelValue", "total-rows", "per-page"]))
            : _createCommentVNode("", true),
          _createVNode(_unref(BAlert), {
            modelValue: _unref(qError),
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(qError) ? (qError).value = $event : null)),
            variant: "danger"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(qError)), 1)
            ]),
            _: 1
          }, 8, ["modelValue"]),
          _createVNode(_unref(BAlert), {
            modelValue: mutationError.value,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((mutationError).value = $event)),
            variant: "danger",
            dismissible: "",
            class: "mt-3",
            onDismissed: _cache[5] || (_cache[5] = ($event: any) => (mutationError.value = null))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(mutationError.value), 1)
            ]),
            _: 1
          }, 8, ["modelValue"])
        ])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})